<template>
  <div class="categories-page">
    <div class="page-header">
      <h2>分类管理</h2>
      <div class="header-actions">
        <el-switch 
          v-model="showHidden" 
          active-text="显示隐藏" 
          @change="loadCategories"
          style="margin-right: 16px"
        />
        <el-button type="primary" @click="openCreateDialog">
          <el-icon><Plus /></el-icon>
          新建分类
        </el-button>
      </div>
    </div>

    <!-- 分类类型切换 -->
    <el-tabs v-model="activeType" @tab-change="loadCategories">
      <el-tab-pane label="支出分类" name="expense" />
      <el-tab-pane label="收入分类" name="income" />
      <el-tab-pane label="账户分类" name="account" />
    </el-tabs>

    <!-- 分类列表 -->
    <div class="category-list">
      <el-collapse v-model="expandedCategories">
        <el-collapse-item 
          v-for="major in categories" 
          :key="major.id" 
          :name="major.id"
          :class="{ 'is-hidden': major.is_hidden }"
        >
          <template #title>
            <div class="major-category-header">
              <div class="category-icon" :style="{ backgroundColor: getCategoryColor(major.category_name) }">
                <el-icon><Folder /></el-icon>
              </div>
              <span class="category-name">{{ major.category_name }}</span>
              <el-tag v-if="major.is_hidden" size="small" type="info">已隐藏</el-tag>
              <span class="children-count" v-if="activeType !== 'account' && major.children?.length">
                {{ major.children.length }} 个子分类
              </span>
              <span class="children-count" v-if="activeType === 'account' && major.children?.length">
                {{ major.children.length }} 个二级分类
              </span>
              <div class="category-actions" @click.stop>
                <el-tooltip :content="major.is_hidden ? '显示' : '隐藏'" v-if="activeType !== 'account'">
                  <el-button link :type="major.is_hidden ? 'success' : 'warning'" size="small" @click="toggleHidden(major, 'major')">
                    <el-icon><component :is="major.is_hidden ? 'View' : 'Hide'" /></el-icon>
                  </el-button>
                </el-tooltip>
                <el-button link type="primary" size="small" @click="openEditDialog(major, 'major')" v-if="activeType !== 'account'">
                  <el-icon><Edit /></el-icon>
                </el-button>
                <el-button link type="primary" size="small" @click="openCreateDialog(major)">
                  <el-icon><Plus /></el-icon>
                </el-button>
                <el-popconfirm title="确定删除此分类？" @confirm="deleteCategory(major, 'major')" v-if="activeType !== 'account'">
                  <template #reference>
                    <el-button link type="danger" size="small">
                      <el-icon><Delete /></el-icon>
                    </el-button>
                  </template>
                </el-popconfirm>
              </div>
            </div>
          </template>
          
          <!-- 小类列表 -->
          <div class="minor-categories" v-if="major.children?.length">
            <div 
              v-for="minor in major.children" 
              :key="minor.id" 
              class="minor-category-item"
              :class="{ 'is-hidden': minor.is_hidden }"
            >
              <div class="category-icon small" :style="{ backgroundColor: getCategoryColor(minor.category_name) }">
                <el-icon><Document /></el-icon>
              </div>
              <span class="category-name">{{ minor.category_name }}</span>
              <el-tag v-if="minor.is_hidden" size="small" type="info">已隐藏</el-tag>
              <span class="usage-count" v-if="activeType === 'account' && minor.usage_count !== undefined">
                使用: {{ minor.usage_count }}
              </span>
              <div class="category-actions">
                <el-tooltip :content="minor.is_hidden ? '显示' : '隐藏'">
                  <el-button link :type="minor.is_hidden ? 'success' : 'warning'" size="small" @click="toggleHidden(minor, 'minor')">
                    <el-icon><component :is="minor.is_hidden ? 'View' : 'Hide'" /></el-icon>
                  </el-button>
                </el-tooltip>
                <el-button link type="primary" size="small" @click="openEditDialog(minor, 'minor', major)">
                  <el-icon><Edit /></el-icon>
                </el-button>
                <el-popconfirm title="确定删除此分类？" @confirm="deleteCategory(minor, 'minor')">
                  <template #reference>
                    <el-button link type="danger" size="small">
                      <el-icon><Delete /></el-icon>
                    </el-button>
                  </template>
                </el-popconfirm>
              </div>
            </div>
          </div>
          <el-empty v-else description="暂无子分类" :image-size="60" />
        </el-collapse-item>
      </el-collapse>
      
      <el-empty v-if="!categories.length" description="暂无分类数据" />
    </div>

    <!-- 新建/编辑弹窗 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="400px" destroy-on-close>
      <el-form :model="formData" label-width="80px">
        <el-form-item label="分类名称" required>
          <el-input v-model="formData.category_name" placeholder="请输入分类名称" />
        </el-form-item>
        <el-form-item label="分类类型" v-if="!editingCategory && activeType !== 'account'">
          <el-radio-group v-model="formData.level">
            <el-radio label="major">大类</el-radio>
            <el-radio label="minor" v-if="activeType === 'expense'">小类</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="所属大类" v-if="formData.level === 'minor' && activeType !== 'account'">
          <el-select v-model="formData.major_category_id" placeholder="选择大类">
            <el-option v-for="cat in categories" :key="cat.id" :label="cat.category_name" :value="cat.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="账户类型" v-if="activeType === 'account' && !editingCategory">
          <el-select v-model="formData.account_type" placeholder="选择账户类型">
            <el-option v-for="cat in categories" :key="cat.id" :label="cat.category_name" :value="cat.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="显示顺序" v-if="activeType === 'account'">
          <el-input-number v-model="formData.display_order" :min="0" :max="999" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveCategory" :loading="saving">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Plus, Edit, Delete, Folder, Document, View, Hide } from '@element-plus/icons-vue'
import api from '../api'

export default {
  name: 'Categories',
  components: { Plus, Edit, Delete, Folder, Document, View, Hide },
  setup() {
    const activeType = ref('expense')
    const categories = ref([])
    const expandedCategories = ref([])
    const dialogVisible = ref(false)
    const saving = ref(false)
    const editingCategory = ref(null)
    const editingLevel = ref(null)
    const showHidden = ref(true)
    
    const formData = reactive({
      category_name: '',
      level: 'major',
      major_category_id: null
    })

    const dialogTitle = computed(() => {
      if (editingCategory.value) return '编辑分类'
      return formData.level === 'major' ? '新建大类' : '新建小类'
    })

    const loadCategories = async () => {
      try {
        if (activeType.value === 'expense') {
          categories.value = await api.getExpenseCategoriesTree(showHidden.value)
        } else if (activeType.value === 'income') {
          categories.value = await api.getIncomeCategoriesTree(showHidden.value)
        } else if (activeType.value === 'account') {
          // 加载账户分类配置
          const accountCategories = await api.getAllAccountCategories(!showHidden.value)
          // 转换为树形结构
          const typeMap = {}
          accountCategories.forEach(cat => {
            if (!typeMap[cat.account_type]) {
              typeMap[cat.account_type] = {
                id: cat.account_type,
                category_name: cat.account_type,
                is_hidden: false,
                children: []
              }
            }
            typeMap[cat.account_type].children.push({
              id: cat.id,
              category_name: cat.subcategory_name,
              display_order: cat.display_order,
              is_hidden: !cat.is_active,
              usage_count: cat.usage_count,
              account_type: cat.account_type
            })
          })
          categories.value = Object.values(typeMap)
        }
      } catch (error) {
        console.error('加载分类失败:', error)
      }
    }

    const getCategoryColor = (name) => {
      const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#fd79a8', '#a29bfe', '#74b9ff']
      let hash = 0
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash)
      }
      return colors[Math.abs(hash) % colors.length]
    }

    const openCreateDialog = (parentCategory = null) => {
      editingCategory.value = null
      editingLevel.value = null
      formData.category_name = ''
      
      if (activeType.value === 'account') {
        // 账户分类只能创建二级分类
        formData.level = 'minor'
        formData.major_category_id = parentCategory?.id || null
        formData.account_type = parentCategory?.id || ''
        formData.display_order = 0
      } else {
        formData.level = parentCategory ? 'minor' : 'major'
        formData.major_category_id = parentCategory?.id || null
      }
      
      dialogVisible.value = true
    }

    const openEditDialog = (category, level, parent = null) => {
      editingCategory.value = category
      editingLevel.value = level
      formData.category_name = category.category_name
      formData.level = level
      formData.major_category_id = parent?.id || category.parent_id
      
      if (activeType.value === 'account') {
        formData.account_type = category.account_type || parent?.id
        formData.display_order = category.display_order || 0
      }
      
      dialogVisible.value = true
    }

    const saveCategory = async () => {
      if (!formData.category_name.trim()) {
        ElMessage.warning('请输入分类名称')
        return
      }

      saving.value = true
      try {
        if (activeType.value === 'account') {
          // 账户分类逻辑
          if (editingCategory.value) {
            // 更新账户分类
            await api.updateAccountCategory(editingCategory.value.id, {
              subcategory_name: formData.category_name,
              display_order: formData.display_order
            })
            ElMessage.success('更新成功')
          } else {
            // 创建账户分类
            if (!formData.account_type) {
              ElMessage.warning('请选择账户类型')
              return
            }
            await api.createAccountCategory(
              formData.account_type,
              formData.category_name,
              formData.display_order || 0
            )
            ElMessage.success('创建成功')
          }
        } else if (editingCategory.value) {
          if (activeType.value === 'expense') {
            if (editingLevel.value === 'major') {
              await api.updateExpenseMajorCategory(editingCategory.value.id, { category_name: formData.category_name })
            } else {
              await api.updateExpenseMinorCategory(editingCategory.value.id, { 
                category_name: formData.category_name,
                major_category_id: formData.major_category_id
              })
            }
          } else {
            await api.updateIncomeMajorCategory(editingCategory.value.id, { category_name: formData.category_name })
          }
          ElMessage.success('更新成功')
        } else {
          if (activeType.value === 'expense') {
            if (formData.level === 'major') {
              await api.createExpenseMajorCategory({ category_name: formData.category_name })
            } else {
              if (!formData.major_category_id) {
                ElMessage.warning('请选择所属大类')
                return
              }
              await api.createExpenseMinorCategory({ 
                category_name: formData.category_name,
                major_category_id: formData.major_category_id
              })
            }
          } else {
            await api.createIncomeMajorCategory({ category_name: formData.category_name })
          }
          ElMessage.success('创建成功')
        }
        
        dialogVisible.value = false
        loadCategories()
      } catch (error) {
        console.error('保存失败:', error)
        ElMessage.error('保存失败，请重试')
      } finally {
        saving.value = false
      }
    }

    const deleteCategory = async (category, level) => {
      try {
        if (activeType.value === 'account') {
          // 账户分类只能删除二级分类
          if (level === 'minor') {
            await api.deleteAccountCategory(category.id)
            ElMessage.success('删除成功')
          } else {
            ElMessage.warning('账户类型不能删除')
            return
          }
        } else if (activeType.value === 'expense') {
          if (level === 'major') {
            await api.deleteExpenseMajorCategory(category.id)
          } else {
            await api.deleteExpenseMinorCategory(category.id)
          }
          ElMessage.success('删除成功')
        } else {
          await api.deleteIncomeMajorCategory(category.id)
          ElMessage.success('删除成功')
        }
        loadCategories()
      } catch (error) {
        console.error('删除失败:', error)
        ElMessage.error(error.response?.data?.detail || '删除失败')
      }
    }

    const toggleHidden = async (category, level) => {
      try {
        const newHiddenState = !category.is_hidden
        if (activeType.value === 'account') {
          // 账户分类只能切换二级分类的状态
          if (level === 'minor') {
            await api.updateAccountCategory(category.id, { is_active: !newHiddenState })
            ElMessage.success(newHiddenState ? '已禁用' : '已启用')
          } else {
            ElMessage.warning('账户类型不能隐藏')
            return
          }
        } else if (activeType.value === 'expense') {
          if (level === 'major') {
            await api.toggleExpenseMajorHidden(category.id, newHiddenState)
          } else {
            await api.toggleExpenseMinorHidden(category.id, newHiddenState)
          }
          ElMessage.success(newHiddenState ? '已隐藏' : '已显示')
        } else {
          await api.toggleIncomeMajorHidden(category.id, newHiddenState)
          ElMessage.success(newHiddenState ? '已隐藏' : '已显示')
        }
        loadCategories()
      } catch (error) {
        console.error('操作失败:', error)
        ElMessage.error('操作失败')
      }
    }

    onMounted(() => {
      loadCategories()
    })

    return {
      activeType, categories, expandedCategories, showHidden,
      dialogVisible, dialogTitle, formData, saving, editingCategory,
      loadCategories, getCategoryColor,
      openCreateDialog, openEditDialog, saveCategory, deleteCategory, toggleHidden
    }
  }
}
</script>

<style scoped>
.categories-page {
  max-width: 800px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 20px;
  color: #333;
}

.header-actions {
  display: flex;
  align-items: center;
}

.category-list {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
}

.major-category-header {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding-right: 20px;
}

.category-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
}

.category-icon.small {
  width: 28px;
  height: 28px;
  font-size: 12px;
}

.category-name {
  font-weight: 500;
  color: #333;
}

.children-count {
  font-size: 12px;
  color: #999;
  margin-left: auto;
}

.usage-count {
  font-size: 12px;
  color: #409eff;
  margin-left: 8px;
}

.category-actions {
  display: flex;
  gap: 4px;
  margin-left: auto;
}

.minor-categories {
  padding: 8px 0 8px 48px;
}

.minor-category-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  transition: background 0.2s;
}

.minor-category-item:hover {
  background: #f5f5f5;
}

.minor-category-item .category-actions {
  opacity: 0;
  transition: opacity 0.2s;
}

.minor-category-item:hover .category-actions {
  opacity: 1;
}

/* 隐藏状态样式 */
.is-hidden {
  opacity: 0.6;
}

.is-hidden .category-icon {
  filter: grayscale(50%);
}

:deep(.el-collapse-item__header) {
  height: auto;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

:deep(.el-collapse-item__content) {
  padding-bottom: 0;
}

:deep(.el-tabs__nav-wrap::after) {
  display: none;
}
</style>
